import { fileToBase64 } from "@iventis/utilities";
import { FileUploadComponent, FileUploadImagePreviewComponent, ConfirmationDialogComponent } from "@iventis/components";
import React, { useEffect, useState } from "react";
import { AsyncThunk, ThunkDispatch, Action } from "@reduxjs/toolkit";
import { Project } from "@iventis/domain-model/model/project";
import { Content } from "@iventis/translations/content/typed-content";
import { ProjectImage } from "@iventis/permissions/src/auth.slice";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { styled } from "@iventis/styles";
import { useDispatch } from "react-redux";

type ApiFunctions = {
    uploadThunk: AsyncThunk<
        Project,
        {
            signal?: AbortSignal;
            image: ProjectImage;
        },
        {
            state: unknown;
        }
    >;
    deleteThunk: AsyncThunk<
        unknown,
        {
            signal?: AbortSignal;
        },
        {
            state: unknown;
        }
    >;
};

export const ProjectImageUploadComponent: React.FC<
    {
        imageUrl?: string;
        fileName?: string;
        removeTitle: string;
        removeMessage: string;
        className?: string;
        disabled?: boolean;
    } & ApiFunctions
> = ({ uploadThunk, deleteThunk, imageUrl, fileName, removeTitle, removeMessage, className, disabled }) => {
    const [confirmation, setConfirmation] = useState<boolean>(false);
    const [imgFileName, setImgFileName] = useState<string>();
    const [loading, setLoading] = useState<boolean>();
    const translate = useIventisTranslate();
    const dispatch = useDispatch<ThunkDispatch<unknown, ApiFunctions[keyof ApiFunctions], Action<unknown>>>();

    useEffect(() => {
        setImgFileName(fileName);
    }, [fileName]);

    const tryUpload = async (file: File) => {
        setImgFileName(file.name);
        setLoading(true);
        const { withHeader } = await fileToBase64(file);
        await dispatch(uploadThunk({ image: { fileName: file.name, imageDataUrl: withHeader } }));
        setLoading(false);
    };
    const tryRemove = async () => {
        setLoading(true);
        await dispatch(deleteThunk({}));
        setLoading(false);
        setImgFileName(undefined);
    };

    return (
        <>
            <StyledFileUploadComponent
                PreviewComponent={StyledImagePreview}
                removeRequested={() => setConfirmation(true)}
                loading={loading}
                fileName={imgFileName}
                fileThumbnailUrl={imageUrl}
                uploadFile={tryUpload}
                uploadButtonText={translate(Content.common.buttons.upload)}
                removeImageText={translate(Content.common.buttons.removeImage)}
                inputAccept="image/*"
                className={className}
                disabled={disabled}
            />
            <ConfirmationDialogComponent
                show={confirmation}
                handleConfirm={() => {
                    setConfirmation(false);
                    tryRemove();
                }}
                handleCancel={() => setConfirmation(false)}
                title={removeTitle}
                message={removeMessage}
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.cancel)}
            />
        </>
    );
};

const StyledImagePreview = styled(FileUploadImagePreviewComponent)`
    .image-preview {
        width: 200px;
        height: 200px;
        object-fit: contain;
    }
`;

const StyledFileUploadComponent = styled(FileUploadComponent)`
    .file-upload-input {
        flex: 1;
        max-width: 200px;
    }
`;
